import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  FileText, 
  Users, 
  Database, 
  TrendingUp,
  Upload,
  Clock
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { format } from 'date-fns';

const API_URL = "";

const COLORS = ['#1d4ed8', '#059669', '#d97706', '#dc2626', '#7c3aed'];

const KPICard = ({ title, value, icon: Icon, trend, color = 'blue' }) => (
  <Card className="kpi-card border-zinc-200 bg-white" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-1">
            {title}
          </p>
          <p className="text-4xl font-black tracking-tighter text-zinc-950">
            {value}
          </p>
          {trend && (
            <p className={`mt-2 text-sm flex items-center gap-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              <TrendingUp className={`w-4 h-4 ${trend < 0 ? 'rotate-180' : ''}`} />
              {Math.abs(trend)}% from last period
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-50`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const { organization } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [fileTypeData, setFileTypeData] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
      
      // Generate sample chart data from recent files
      if (response.data.recent_files?.length > 0) {
        const chartDataPoints = response.data.recent_files.map((file, index) => ({
          name: file.filename?.substring(0, 10) || `File ${index + 1}`,
          rows: file.row_count || 0
        })).reverse();
        setChartData(chartDataPoints);

        // Generate file type distribution
        const typeCount = {};
        response.data.recent_files.forEach(file => {
          const type = file.file_type?.toUpperCase() || 'OTHER';
          typeCount[type] = (typeCount[type] || 0) + 1;
        });
        setFileTypeData(Object.entries(typeCount).map(([name, value]) => ({ name, value })));
      }
    } catch (e) {
      console.error('Error fetching dashboard data:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight text-zinc-950">
          Dashboard
        </h1>
        <p className="text-zinc-500 mt-1">
          Welcome to {organization?.name || 'SIL Insights'}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard 
          title="Total Files" 
          value={stats?.file_count || 0} 
          icon={FileText}
          color="blue"
        />
        <KPICard 
          title="Total Users" 
          value={stats?.user_count || 0} 
          icon={Users}
          color="green"
        />
        <KPICard 
          title="Data Rows" 
          value={(stats?.total_rows || 0).toLocaleString()} 
          icon={Database}
          color="amber"
        />
        <KPICard 
          title="Recent Uploads" 
          value={stats?.recent_files?.length || 0} 
          icon={Upload}
          color="purple"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Bar Chart - File Rows */}
        <Card className="lg:col-span-2 border-zinc-200 bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-zinc-950">
              Rows per File
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#71717a" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#71717a" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e4e4e7',
                        borderRadius: '8px'
                      }} 
                    />
                    <Bar dataKey="rows" fill="#1d4ed8" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-400">
                  No data available. Upload files to see charts.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart - File Types */}
        <Card className="border-zinc-200 bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-zinc-950">
              File Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {fileTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={fileTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {fileTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-400">
                  No files uploaded yet.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Files Table */}
      <Card className="border-zinc-200 bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-zinc-950 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Recent Uploads
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="recent-files-table">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">
                    Filename
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">
                    Type
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">
                    Rows
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">
                    Uploaded
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats?.recent_files?.length > 0 ? (
                  stats.recent_files.map((file, index) => (
                    <tr key={file.id || index} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 text-sm text-zinc-950 font-medium">
                        {file.filename}
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-block px-2 py-1 text-xs font-semibold uppercase rounded bg-zinc-100 text-zinc-600">
                          {file.file_type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-zinc-600 text-right tabular-nums">
                        {(file.row_count || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-sm text-zinc-500 text-right">
                        {file.created_at ? format(new Date(file.created_at), 'MMM d, HH:mm') : '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-zinc-400">
                      No files uploaded yet. Start by uploading a file.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
